import type { TFunction } from 'i18next';

import {
  IMAGE_CHECK_FOLD_TARGET,
  IMAGE_CHECK_STAGE,
  isHiddenStage,
} from '../constants/hidden-stages';
import type { LineageItem, ProductDisposition, Stage } from '../types/lineage-images.api.types';
import type { ProductLineageRunLabel } from '../types/product-master.api.types';
import type {
  RunProgress,
  RunRead,
  RunSummary,
  RunSummaryParent,
  StageProgress,
} from '../types/run.api.types';

import { humanizeCode } from './catalog-labels';
import { groupLineageByRun } from './product-master-detail';

/**
 * The post-Matching stages Product history shows after the ledger ones (approved design:
 * NAMES_AND_HISTORY_APPROVED.md — "stages after Matching included"). The ledger only ever records
 * IMPORT / IMAGE_GATE / CURATION (catalog `run_items`); these live in the workflow /
 * content-generation services instead, so their per-batch status is read separately
 * (`stageProgressOf` for the row, the journey drawer's own reads for the exact per-product figure).
 */
export const JOURNEY_CONTENT_STAGES = [
  'PRE_ENRICH_SCORE',
  'SAMPLE',
  'ENRICHMENT',
  'POST_ENRICH_SCORE',
  'APPROVAL',
  'DELIVERY',
] as const satisfies readonly Stage[];

/** The three stages the catalog lineage ledger records today — exact, per-product, every attempt. */
export const JOURNEY_LEDGER_STAGES = [
  'IMPORT',
  'IMAGE_GATE',
  'CURATION',
] as const satisfies readonly Stage[];

/**
 * This batch's own row for a post-Matching stage, from `RunProgress.stages[]` (run-wide, but the
 * real per-stage status FastAPI tracks — not a heuristic) — cheap enough for every row; the journey
 * drawer fetches the exact per-product figure on click. `undefined` while `RunProgress` hasn't
 * loaded yet, or on an older deploy without it.
 */
export function stageProgressOf(
  progress: RunProgress | undefined,
  stage: Stage,
): StageProgress | undefined {
  return progress?.stages.find((s) => s.stage === stage);
}

/** One batch (catalog run) this product went through — the ledger stages exact and per-attempt,
 *  the run record resolved separately (so the row can render before it loads). */
export interface ProductBatchEntry {
  runId: string;
  /** Newest item time in this batch — the row's date. */
  when: string;
  markets: string[];
  /** IMPORT's disposition (first market) — "New" / "Changed" / "Already curated" etc. */
  disposition: ProductDisposition | null;
  /** Every ledger stage this batch touched was SKIPPED — "Skipped: already matched" row. */
  allSkipped: boolean;
  /** Every IMPORT / IMAGE_GATE / CURATION item of this batch, every attempt. */
  ledgerItems: LineageItem[];
  /** Ledger stages present, in flow order. */
  ledgerStages: Stage[];
  /** What the row draws of its run (name, status, stages, parent id), from the batch's summaries
   *  row — or, on an older catalog-service, its `RunRead` + `RunProgress`; undefined until read. */
  view: BatchRunView | undefined;
  /** The gate this batch waits at (drives [Review]), from whichever read answered it first. */
  pendingGateStage: string | null;
  /** This product's run label from the lineage page read (name / status / pendingGate), when sent. */
  label: ProductLineageRunLabel | undefined;
  /** True while this row's read(s) are still pending (only inside the visible window — a batch past
   *  it is simply `false`, not "loading", since no read for it is in flight). */
  viewIsLoading: boolean;
  /** True once a read this row depends on failed (or the server omitted the run) — an explicit
   *  state, never a silently empty row. */
  viewIsError: boolean;
  /** Retries every read this row depends on — what a row's own "Couldn't load — Retry" calls. */
  refetchStatus: () => void;
}

/** The parent batch's facts a "Re-run of … (that batch: …)" backlink renders. */
export interface BatchParentView {
  name: string | null;
  status: string;
  currentStage: string | null;
  failureReason: string | null;
  startedAt: string | null;
}

/** One batch's run as its history row renders it — one shape for both read paths, so the row is
 *  drawn by the exact same code whether it came from summaries or from `RunRead` + `RunProgress`. */
export interface BatchRunView extends BatchParentView {
  parentRunId: string | null;
  /** The run's declared flow (else the default list) — `flowStagesFor`'s answer. */
  flowStages: Stage[];
  /** `/progress` `stages[].status` per stage (first row wins, as `stageProgressOf` reads it). */
  stageStatus: ReadonlyMap<string, string>;
  /** The newest DELIVERY job delivered (status DELIVERED or a delivered count above 0). */
  delivered: boolean;
}

/** First status per stage, exactly what `Array.find` over `stages[]` would return. */
function stageStatusMap(stages: readonly { stage: string; status: string }[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of stages) if (!map.has(row.stage)) map.set(row.stage, row.status);
  return map;
}

/** Delivered per the row's outcome sentence: the job says DELIVERED, or delivered anything. */
function isDelivered(delivery: { status: string; delivered?: number | null } | null | undefined) {
  return delivery?.status === 'DELIVERED' || (delivery?.delivered ?? 0) > 0;
}

/** Fallback path: the view from `RunRead` (+ `RunProgress` once it loaded) — today's fields. */
export function batchViewFromRun(run: RunRead, progress: RunProgress | undefined): BatchRunView {
  return {
    name: run.name ?? null,
    status: run.status,
    currentStage: run.currentStage ?? null,
    failureReason: run.failureReason ?? null,
    startedAt: run.startedAt ?? null,
    parentRunId: run.parentRunId ?? null,
    flowStages: flowStagesFor(run),
    stageStatus: stageStatusMap(progress?.stages ?? []),
    delivered: isDelivered(progress?.delivery),
  };
}

/**
 * Summaries path: the same view from one `RunSummary`. Declared stages are the first
 * `stageProgress.total` of `stages` (the server lists the flow's declared stages first, then any
 * undeclared ledger stage) — `config.flow.stages` exactly; no flow → the same default list.
 */
export function batchViewFromSummary(summary: RunSummary): BatchRunView {
  const declared = summary.stageProgress
    ? summary.stages.slice(0, summary.stageProgress.total).map((s) => s.stage)
    : [];
  return {
    name: summary.name,
    status: summary.status,
    currentStage: summary.currentStage,
    failureReason: summary.failureReason,
    startedAt: summary.startedAt,
    parentRunId: summary.parentRunId,
    flowStages: flowStagesOrDefault(declared),
    stageStatus: stageStatusMap(summary.stages),
    delivered: isDelivered(summary.delivery),
  };
}

/** Summaries path: the labelled parent a summary carries, as the backlink reads it. */
export function parentViewFromSummary(parent: RunSummaryParent): BatchParentView {
  return {
    name: parent.name,
    status: parent.status,
    currentStage: parent.currentStage,
    failureReason: parent.failureReason,
    startedAt: parent.startedAt,
  };
}

/** Fallback path: a parent's own `RunRead`, as the backlink reads it. */
export function parentViewFromRun(run: RunRead): BatchParentView {
  return {
    name: run.name ?? null,
    status: run.status,
    currentStage: run.currentStage ?? null,
    failureReason: run.failureReason ?? null,
    startedAt: run.startedAt ?? null,
  };
}

/** Groups this product's lineage items into one row per batch, newest first unless `ascending`. */
export function buildProductBatches(items: LineageItem[], ascending = false): ProductBatchEntry[] {
  return groupLineageByRun(items, ascending).map((entry) => ({
    runId: entry.runId,
    when: entry.when,
    markets: entry.markets,
    disposition: entry.disposition,
    allSkipped: entry.stages.length > 0 && entry.stages.every((s) => s.status === 'SKIPPED'),
    ledgerItems: entry.items,
    ledgerStages: entry.stages.map((s) => s.stage),
    view: undefined,
    pendingGateStage: null,
    label: undefined,
    viewIsLoading: false,
    viewIsError: false,
    refetchStatus: () => {
      // No query is in flight for a batch outside the visible window — nothing to retry yet.
    },
  }));
}

/** "1st" / "2nd" / "3rd" / "4th" — plain English ordinal for the attempts fold; not translated
 *  (the sentence around it is), matching how the rest of the app numbers attempts ("attempt 2"). */
export function ordinal(n: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  const suffix = suffixes[(v - 20) % 10] ?? suffixes[v] ?? 'th';
  return `${String(n)}${suffix}`;
}

/** A bare UUID, case-insensitive — what `errorCode`/`skipReason` carry for the run SUPERSEDED_BY_LATER_RUN / RUN_PRODUCT_HELD reference today. */
const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** The batch id embedded in a SUPERSEDED_BY_LATER_RUN / RUN_PRODUCT_HELD item's reason fields,
 *  wherever today's (unfixed) data happens to carry it. Private — callers only ever want the
 *  classified `batchReferenceReason` below, never a bare id with no idea what it means. */
function referencedBatchId(item: Pick<LineageItem, 'errorCode' | 'skipReason'>): string | null {
  const text = `${item.skipReason ?? ''} ${item.errorCode ?? ''}`;
  return UUID_PATTERN.exec(text)?.[0] ?? null;
}

/** Which of the two codes an item's embedded batch id belongs to, so the caller can pick the
 *  right clean phrase instead of ever rendering the humanized raw UUID. */
export type BatchReferenceKind = 'held' | 'superseded';

export interface BatchReferenceReason {
  kind: BatchReferenceKind;
  runId: string;
}

/**
 * A SUPERSEDED_BY_LATER_RUN / RUN_PRODUCT_HELD item, read as "which batch, and why" — never the
 * raw reason text, which on this branch still humanizes the embedded UUID into garbage ("A1b2c3d4
 * e5f6 …"). A stand-in for track W3's upcoming `lineageReasonRunId` (landing in
 * `utils/lineage-images-format.ts`, which this file must not edit) — keep this the ONE place that
 * does this classification so it can be pointed at W3's real export at merge, instead of being
 * duplicated across the journey drawer and the product history card.
 *
 * Today's (unfixed) data: RUN_PRODUCT_HELD's `errorCode` carries that literal code and `skipReason`
 * carries "held by run <id>" prose; SUPERSEDED_BY_LATER_RUN's `skipReason` carries that literal
 * code and `errorCode` carries the superseding run's bare id — so each code is read from the one
 * field that holds it, and the id is pulled from the other.
 */
export function batchReferenceReason(
  item: Pick<LineageItem, 'errorCode' | 'skipReason'>,
): BatchReferenceReason | null {
  const runId = referencedBatchId(item);
  if (!runId) return null;
  // HELD carries its code in errorCode (id in skipReason prose); SUPERSEDED the reverse.
  if (item.errorCode === 'RUN_PRODUCT_HELD') return { kind: 'held', runId };
  if (item.skipReason === 'SUPERSEDED_BY_LATER_RUN') return { kind: 'superseded', runId };
  return null;
}

/**
 * This batch's own flow, in order — `run.config.flow.stages` when it loaded (the run's actual
 * declared flow, so "N of M" and the bar always match what this specific batch runs), else the
 * default 9-stage ledger+content list every flow in practice follows (an older/unloaded `run`).
 */
export function flowStagesFor(run: Pick<RunRead, 'config'> | undefined): Stage[] {
  return flowStagesOrDefault(run?.config?.flow?.stages);
}

/** A run's declared stages when it has any, else the default ledger + content list. */
export function flowStagesOrDefault(declared: readonly Stage[] | null | undefined): Stage[] {
  if (declared && declared.length > 0) return [...declared];
  return [...JOURNEY_LEDGER_STAGES, ...JOURNEY_CONTENT_STAGES];
}

/** A stage segment's state in the product-history / journey-drawer bar (design round 2, items 4B/7). */
export type StageBarState = 'pending' | 'current' | 'done' | 'failed' | 'skipped';

/**
 * Collapses the two status vocabularies this app reads a stage's outcome from (ledger
 * `ItemStatus`: PENDING/SUCCESS/FAILED/SKIPPED/REVISIT; `StageProgress.status`: PENDING/RUNNING/
 * AWAITING_GATE/COMPLETED/FAILED/SKIPPED/RECONCILE_FAILED) into the five bar states. REVISIT is a
 * PROBLEM, not work in progress — same call `isProblemStatus` (`utils/lineage-chip-tone.ts`) makes
 * for a lineage item — so it maps to `failed`, matching `glyphFor`/`STATUS_ICON`'s warning glyph
 * for it everywhere else in this app.
 */
export function stageBarState(status: string | undefined): StageBarState {
  switch (status) {
    case 'SUCCESS':
    case 'COMPLETED':
      return 'done';
    case 'FAILED':
    case 'RECONCILE_FAILED':
    case 'REVISIT':
      return 'failed';
    case 'RUNNING':
    case 'AWAITING_GATE':
      return 'current';
    case 'SKIPPED':
      return 'skipped';
    default:
      return 'pending';
  }
}

/** Run statuses where the batch is no longer actively processing anything (whether it finished
 *  clean or stopped) — a `current` (amber) segment never makes sense once the run itself is here. */
const TERMINAL_RUN_STATUSES = new Set([
  'COMPLETED',
  'FAILED',
  'REJECTED',
  'TERMINATED',
  'RESET',
  'SKIPPED',
]);

/** Of the terminal statuses, the ones that mean the batch stopped on a problem — a lingering
 *  `current` segment becomes `failed` (red) for these, `done` (neutral) for the rest. */
const FAILED_LIKE_RUN_STATUSES = new Set(['FAILED', 'REJECTED', 'TERMINATED']);

/** True once a batch stopped on a problem (never finished its flow) — the drawer's "not reached
 *  yet" line reads "not reached (batch stopped at X)" instead of the plain "not reached yet" only
 *  for these (design round 2, item 7 follow-up); a batch that can still progress, or finished
 *  clean, keeps the plain wording. */
export function isStoppedRunStatus(status: string | undefined): boolean {
  return !!status && FAILED_LIKE_RUN_STATUSES.has(status);
}

/** How bad a bar state reads — a folded Feed intake segment/row shows the worse of its own state
 *  and the hidden Image check's, so a product stopped at the image step never reads as a clean
 *  intake. Shared by the stage bar and the product page's Journey rows. */
export const STAGE_BAR_SEVERITY: Record<StageBarState, number> = {
  pending: 0,
  done: 1,
  skipped: 2,
  current: 3,
  failed: 4,
};

/** The worse of two bar states (`STAGE_BAR_SEVERITY`). */
export function worseBarState(a: StageBarState, b: StageBarState): StageBarState {
  return STAGE_BAR_SEVERITY[b] > STAGE_BAR_SEVERITY[a] ? b : a;
}

/** One segment of the stage bar. */
export interface StageBarSegment {
  stage: Stage;
  state: StageBarState;
}

/** A built bar: one segment per flow stage, plus "N of M" — N = every segment this batch actually
 *  reached (done, failed, current or skipped; only `pending` stages are still ahead). */
export interface StageBar {
  segments: StageBarSegment[];
  reached: number;
  total: number;
}

/**
 * Builds the bar for one batch: `statusOf(stage)` reads the ledger's newest-attempt status for a
 * ledger stage, or `StageProgress.status` for a content stage — the caller supplies it since the
 * two live in different reads (exact per-product ledger items vs. the batch's own `RunProgress`).
 *
 * `runStatus` (the batch's own `RunRead.status`/`RunProgress.runStatus`) corrects a stale
 * `current` segment once the BATCH itself is no longer live: a stage whose own per-product record
 * never got a final status (still reads RUNNING/AWAITING_GATE/REVISIT) but whose batch already
 * stopped would otherwise show a misleading amber "in progress" dot forever. Omitted (or an
 * active status), every segment keeps its own `stageBarState` untouched.
 */
export function buildStageBar(
  stages: readonly Stage[],
  statusOf: (stage: Stage) => string | undefined,
  runStatus?: string,
): StageBar {
  const isTerminal = !!runStatus && TERMINAL_RUN_STATUSES.has(runStatus);
  const isFailedLike = !!runStatus && FAILED_LIKE_RUN_STATUSES.has(runStatus);
  const stateOf = (stage: Stage): StageBarState => {
    const state = stageBarState(statusOf(stage));
    return state === 'current' && isTerminal ? (isFailedLike ? 'failed' : 'done') : state;
  };
  // The hidden Image check gets no segment of its own (hidden-stages.ts): once Feed intake has
  // finished, its outcome folds into Feed intake's segment — worse state wins — and "N of M"
  // counts only the segments shown.
  const segments = stages
    .filter((stage) => !isHiddenStage(stage))
    .map((stage) => {
      const own = stateOf(stage);
      if (
        stage !== IMAGE_CHECK_FOLD_TARGET ||
        own !== 'done' ||
        !stages.includes(IMAGE_CHECK_STAGE)
      ) {
        return { stage, state: own };
      }
      return { stage, state: worseBarState(own, stateOf(IMAGE_CHECK_STAGE)) };
    });
  const reached = segments.filter((s) => s.state !== 'pending').length;
  return { segments, reached, total: segments.length };
}

/** One ledger stage's retry history for one market — only emitted when it was a REAL retry (more
 *  than one attempt, and at least one of them failed); a stage that simply succeeded on the first
 *  try, or retried without ever failing, has nothing worth a ↻ line (design round 2, item 4B). */
export interface StageRetryGroup {
  stage: Stage;
  /** This group's market (ledger `scope`), e.g. "en~GB". */
  scope: string;
  /** Oldest first. */
  attempts: LineageItem[];
}

/** Every real retry group of one ledger stage in this batch, one per market that had one. */
export function stageRetryGroups(
  entry: Pick<ProductBatchEntry, 'ledgerItems'>,
  stage: Stage,
): StageRetryGroup[] {
  const byScope = new Map<string, LineageItem[]>();
  for (const item of entry.ledgerItems) {
    if (item.stage !== stage) continue;
    byScope.set(item.scope, [...(byScope.get(item.scope) ?? []), item]);
  }
  const groups: StageRetryGroup[] = [];
  for (const [scope, items] of byScope) {
    const attempts = [...items].sort((a, b) => a.attempt - b.attempt);
    if (attempts.length > 1 && attempts.some((a) => a.status === 'FAILED')) {
      groups.push({ stage, scope, attempts });
    }
  }
  return groups;
}

/** A batch outcome sentence's colour: `error` for a stopped-on-a-problem batch, `ok` once it is
 *  cleanly done, `warning` while it waits on the user, `muted` for every other live/neutral state. */
export type RunOutcomeTone = 'ok' | 'warning' | 'error' | 'muted';

export interface RunOutcome {
  text: string;
  tone: RunOutcomeTone;
}

/**
 * One batch's own outcome sentence, from its `RunRead.status` / `RunProgress.runStatus` alone —
 * covers every value `types/runs.types.ts`'s `RUN_LIST_TABS` names a run by (PENDING / RUNNING /
 * AWAITING_GATE / COMPLETED / DELIVERING / FAILED / REJECTED / RESET / TERMINATED / SKIPPED) plus a
 * neutral fallback for a status this app doesn't know about yet — NEVER an empty/missing sentence
 * for a batch that has a status. `status` is read as a plain `string` on purpose: `RunRead.status`'s
 * generated type only names the 8 values the run record itself persists, but `REJECTED`/
 * `DELIVERING` are real values the wire can still send (they're `FAILED`/`COMPLETED` refined by
 * `failureCode`/delivery state server-side) — the same "treat it as a string, not the narrow union"
 * rule `RunFailureCode` already documents.
 *
 * Does not know about an open gate: a caller that tracks `pendingGate` checks that FIRST (a gate
 * waiting on the user always wins and isn't reflected in `status` at all) and only falls through to
 * this for every other case.
 *
 * Returns the "that batch: " prefixed form — what the re-run backlink (`parentOutcomeText`) wants
 * verbatim; a caller narrating its OWN batch's outcome strips the prefix itself, same convention
 * `batchOutcomeText` already used for `thatBatchFailed`.
 */
export function runStatusOutcome(
  t: TFunction<'catalog-v2-product-master'>,
  status: string,
  stage: string,
  reason: string | null,
  delivered: boolean,
): RunOutcome {
  switch (status) {
    case 'FAILED':
      return {
        text: t('productHistory.thatBatchFailed', { stage, reason: reason ?? '' }),
        tone: 'error',
      };
    case 'TERMINATED':
      return { text: t('productHistory.thatBatchStopped', { stage }), tone: 'error' };
    case 'REJECTED':
      return {
        text: reason
          ? t('productHistory.thatBatchRejectedReason', { stage, reason })
          : t('productHistory.thatBatchRejected', { stage }),
        tone: 'error',
      };
    case 'RESET':
      return { text: t('productHistory.thatBatchReset'), tone: 'muted' };
    case 'PENDING':
      return { text: t('productHistory.thatBatchPending'), tone: 'muted' };
    case 'DELIVERING':
      return { text: t('productHistory.thatBatchDelivering'), tone: 'muted' };
    case 'SKIPPED':
      return { text: t('productHistory.thatBatchSkippedStatus'), tone: 'muted' };
    case 'RUNNING':
      return { text: t('productHistory.thatBatchRunning', { stage }), tone: 'muted' };
    case 'AWAITING_GATE':
      return { text: t('productHistory.thatBatchWaiting', { stage }), tone: 'warning' };
    case 'COMPLETED':
      return {
        text: delivered
          ? t('productHistory.thatBatchDone', { outcome: t('productHistory.delivered') })
          : t('productHistory.matchedNotPublished'),
        tone: 'ok',
      };
    default:
      return {
        text: t('productHistory.thatBatchUnknownStatus', { status: humanizeCode(status) }),
        tone: 'muted',
      };
  }
}
